import * as React from 'react'

import { selectCurrentUser } from '@user/store/authorization'
import {
  DEFAULT_COMPANY_HEADER_DISPLAY_SETTINGS,
  getCompanyHeaderDisplaySettings,
} from '@user/store/companyHeaderDisplaySettings'
import { setCompanyHeaderDisplaySettings } from '@user/store/companyHeaderDisplaySettings/slice'
import { getCompanySetup } from '@user/store/companySetup/api'
import { resetCompanySetup } from '@user/store/companySetup/slice'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { useAppSelector } from '@user/store/hooks/useAppSelector'

import { hasAnyEnabledField } from '../buildCompanyIdentifier'

/**
 * Loads the company header display settings once per app start (and again
 * whenever the tenant changes), then loads `/company` only when at least one
 * field is enabled. Both slices are reset first so a previous tenant's data can
 * never show. Failures are deliberately silent: a rejected fetch leaves the
 * reset (empty) state, which renders no identifier. That is the decided
 * behavior, not a swallowed error. Thunk rejections resolve rather than throw,
 * so no try/catch is needed. In-flight requests are aborted on cleanup so a
 * late response from a previous tenant can never reach the store.
 */
export const useLoadCompanyIdentifierOnMount = (authenticated: boolean) => {
  const dispatch = useAppDispatch()
  const tenantId = useAppSelector(selectCurrentUser)?.tenantId

  React.useEffect(() => {
    if (!authenticated || !tenantId) return
    let mounted = true

    dispatch(
      setCompanyHeaderDisplaySettings(DEFAULT_COMPANY_HEADER_DISPLAY_SETTINGS),
    )
    dispatch(resetCompanySetup())

    const pending: { abort: () => void }[] = []

    const execute = async () => {
      const settingsRequest = dispatch(getCompanyHeaderDisplaySettings())
      pending.push(settingsRequest)
      const result = await settingsRequest
      if (
        !mounted ||
        !getCompanyHeaderDisplaySettings.fulfilled.match(result)
      ) {
        return
      }
      if (hasAnyEnabledField(result.payload)) {
        const companyRequest = dispatch(getCompanySetup())
        pending.push(companyRequest)
        await companyRequest
      }
    }
    execute()

    return () => {
      mounted = false
      pending.forEach((request) => request.abort())
    }
  }, [dispatch, authenticated, tenantId])
}
